import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle2, Clock3, Eye, FileText, Printer, ReceiptText, ScrollText, UserCircle2 } from 'lucide-react';
import {
  activityActionLabels,
  activityResultLabels,
  documentTypeLabels,
  formatActivityTimestamp,
} from '@/hooks/useOrderDocumentWorkflow';

const documentTypeIcons = {
  kitchen_ticket: ReceiptText,
  receipt: ScrollText,
};

export default function OrderDocumentsPanel({
  selectedOrder,
  currency,
  activityLoading,
  documentActivities,
  openDocumentPreview,
  queueDocumentAction,
  hasDocumentActivity,
  handleReprint,
}) {
  return (
    <>
      <div className="rounded-lg border border-border/70 bg-card p-4">
        <div className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">Documents</div>
        <div className="space-y-3">
          {['kitchen_ticket', 'receipt'].map((docType) => {
            const DocIcon = documentTypeIcons[docType] || FileText;
            return (
              <div key={docType} className="rounded-md border border-border/60 bg-background/60 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <DocIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="text-sm font-semibold">{documentTypeLabels[docType]}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2.5 text-xs"
                    onClick={() => openDocumentPreview(selectedOrder.id, docType)}
                    data-testid={docType === 'kitchen_ticket' ? `preview-kitchen-ticket-${selectedOrder.id}` : `preview-receipt-${selectedOrder.id}`}
                  >
                    <Eye className="me-1 h-3.5 w-3.5" /> Preview
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2.5 text-xs"
                    onClick={() => queueDocumentAction(selectedOrder.id, docType, 'pdf')}
                    data-testid={`save-pdf-${docType}-${selectedOrder.id}`}
                  >
                    <FileText className="me-1 h-3.5 w-3.5" /> Save PDF
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2.5 text-xs"
                    onClick={() => queueDocumentAction(selectedOrder.id, docType, 'print')}
                    data-testid={`print-${docType}-${selectedOrder.id}`}
                  >
                    <Printer className="me-1 h-3.5 w-3.5" /> Browser Print
                  </Button>
                  {hasDocumentActivity(docType) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2.5 text-xs"
                      onClick={() => handleReprint(selectedOrder.id, docType)}
                      data-testid={docType === 'kitchen_ticket' ? `reprint-kitchen-ticket-${selectedOrder.id}` : `reprint-receipt-${selectedOrder.id}`}
                    >
                      Open Again
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-border/70 bg-card p-4">
        <div className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">Document Activity</div>
        {activityLoading ? (
          <div className="text-sm text-muted-foreground">Loading activity...</div>
        ) : documentActivities.length === 0 ? (
          <div className="text-sm text-muted-foreground">No document activity yet.</div>
        ) : (
          <div className="max-h-[320px] overflow-y-auto rounded-md border border-border/60">
            <div className="divide-y divide-border/60">
              {documentActivities.map((activity) => {
                const isSuccess = ['success', 'generated'].includes(activity.result);
                const isRequested = activity.result === 'requested';
                return (
                  <div key={activity.id} className="grid grid-cols-[auto_1fr_auto] items-start gap-3 px-3 py-2.5 text-xs">
                    <div className="mt-0.5">
                      {isSuccess ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : isRequested ? (
                        <Clock3 className="h-4 w-4 text-amber-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-sm font-semibold">
                        <span>{documentTypeLabels[activity.document_type] || activity.document_type}</span>
                        <span className="text-muted-foreground">•</span>
                        <span className="font-medium text-muted-foreground">{activityActionLabels[activity.action_type] || activity.action_type}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" />{formatActivityTimestamp(activity.timestamp)}</span>
                        <span className="inline-flex items-center gap-1"><UserCircle2 className="h-3.5 w-3.5" />{activity.performed_by?.name || activity.performed_by?.email || 'System'}</span>
                      </div>
                    </div>
                    <Badge className={isSuccess ? 'bg-green-100 text-green-800' : isRequested ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                      {activityResultLabels[activity.result] || activity.result}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
